import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import amplitude from 'amplitude-js';
import { toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { MarkedList } from 'apps/auth/components/common/MarkedList/MarkedList';
import { MarkedListItem } from 'apps/auth/components/common/MarkedList/MarkedListItem';
import OnfidoDocList from 'apps/auth/components/common/onfido/OnfidoDocList';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import BiometricTerms from 'apps/auth/components/kyc/BiometricTerms';
import BasePage from 'apps/auth/components/utils/BasePage';
import {
    AF_ONFIDO_IN_REVIEW_PROMO_GOT_IT_CLICKED,
    AF_REQUESTING_IDENTITY_DOCUMENT_DOCUMENT_SELECTED,
    AF_REQUESTING_IDENTITY_DOCUMENT_FAQ,
    AF_REQUESTING_IDENTITY_DOCUMENT_PROMO_SCREEN_SHOWN,
    AF_REQUESTING_IDENTITY_DOCUMENT_SUBMITTED_SHOWN,
    AF_REQUESTING_IDENTITY_MOTION_SUBMITTED_SHOWN,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { SCENARIO_ID } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import {
    KYC_BIOMETRIC_DISCLAIMER_SCREEN,
    KYC_FIELDS_SCREEN,
    KYC_GUIDE_SCREEN,
    KYC_INTRO_SCREEN,
    KYC_VERIFYING_SCREEN,
    ONFIDO_DOCUMENT_EXPLANATION_SCREEN,
    ONFIDO_DOCUMENTS_SCREEN,
    ONFIDO_SDK,
} from 'apps/auth/constants/ComponentNames';
import { ERROR } from 'apps/auth/constants/Constants';
import { AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { addOnfido } from 'apps/auth/helpers/DOM';
import { useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import { OnfidoService } from 'apps/auth/services/OnfidoService';

import { OnfidoContainer } from './OnfidoFramePage';

const AntiFraudOnfidoPage = (props) => {
    const messages = usePageMessages();
    const extra = useExtra();
    const { countryId, ballId } = useAuth();
    const location = useLocation();
    const { docs, countryInEEA, fraudScenarioId, biometricTermsDisclaimer } = useExtra();
    const onfidoOutRef = useRef(null);
    const popupRef = useRef(null);
    const biometricTerms = useRef(biometricTermsDisclaimer);
    const [showOnfido, setShowOnfido] = useState(false);

    const [currentScreen, setCurrentScreen] = useState(KYC_INTRO_SCREEN);
    const { theme } = useThemeDetector();

    useEffect(() => {
        if (showOnfido && typeof window !== 'undefined') {
            if (typeof window.Onfido === 'undefined') {
                addOnfido(() => {
                    initOnfido();
                });
            } else {
                initOnfido();
            }
        }
        return () => {
            onfidoOutRef.current?.tearDown();
        };
    }, [showOnfido]);

    const initOnfido = () => {
        let options = OnfidoService.getFraudOptions(extra.docInfo, extra.docs);
        onfidoOutRef.current = OnfidoService.init(window.Onfido, extra.onfidoAccessToken, options, onComplete, onError, theme);
    };
    const getBackUrl = () => {
        if (currentScreen === KYC_FIELDS_SCREEN || currentScreen === KYC_GUIDE_SCREEN) {
            return AUTH_SEND_MAIN_LINK;
        }
        return '';
    };

    const onClickIcon = (e) => {
        if (currentScreen === ONFIDO_DOCUMENT_EXPLANATION_SCREEN) {
            e.preventDefault();
            setCurrentScreen(ONFIDO_DOCUMENTS_SCREEN);
        }
    };

    const onClickDocument = (docType) => {
        amplitude.getInstance().logEvent(AF_REQUESTING_IDENTITY_DOCUMENT_DOCUMENT_SELECTED, { [SCENARIO_ID]: fraudScenarioId });
        props.toggleSpinner(true);
        apiFetchData(`${location.pathname}/${docType}`)
            .then((json) => {
                if (json) {
                    if (json.messageBodyMessages) {
                        props.updateMessages(json.messageBodyMessages);
                    }
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        props.setExtra(json.extra);
                    }
                }
                props.toggleSpinner(false);
                setCurrentScreen(ONFIDO_DOCUMENT_EXPLANATION_SCREEN);
            })
            .catch(() => {
                props.toggleSpinner(false);
                setCurrentScreen(ERROR);
            });
    };

    const onContinueIntro = (_e, skipBiometric = false) => {
        amplitude.getInstance().logEvent(AF_REQUESTING_IDENTITY_DOCUMENT_PROMO_SCREEN_SHOWN, { [SCENARIO_ID]: fraudScenarioId });
        if (skipBiometric) {
            biometricTerms.current = false;
        }
        if (biometricTerms.current) {
            setCurrentScreen(KYC_BIOMETRIC_DISCLAIMER_SCREEN);
        } else if (extra.docs?.identificationRequired) {
            setCurrentScreen(ONFIDO_DOCUMENTS_SCREEN);
        } else {
            openOnfido();
        }
    };

    const onComplete = () => {
        apiFetchData(`${location.pathname}/uploaded`).then((_json) => {
            const amplitudeEventName = extra.docs ? AF_REQUESTING_IDENTITY_DOCUMENT_SUBMITTED_SHOWN : AF_REQUESTING_IDENTITY_MOTION_SUBMITTED_SHOWN;
            const successMessage = extra.docs ? messages.documentSubmittedSpinner : messages.submittedSpinner;
            props.toggleModalWithTimer(true, successMessage, () => {
                amplitude.getInstance().logEvent(amplitudeEventName, { [SCENARIO_ID]: fraudScenarioId });
                setCurrentScreen(KYC_VERIFYING_SCREEN);
            });
        });
    };

    const onError = (data) => {
        Sentry.captureMessage('Onfido Frame error', {
            extra: {
                ball_id: ballId || '',
                countryId: countryId,
                data: data,
            },
            level: 'error',
        });

        if (data.message !== undefined) {
            window.location.href = extra.failURL + '?customErrorInfo=' + data.message;
        } else {
            window.location.href = extra.failURL;
        }
    };

    const openOnfido = () => {
        setCurrentScreen(ONFIDO_SDK);
        setShowOnfido(true);
    };

    const componentMap = {
        [KYC_BIOMETRIC_DISCLAIMER_SCREEN]: (
            <BiometricTerms onContinueIntro={onContinueIntro} onCloseIconClick={() => setCurrentScreen(KYC_INTRO_SCREEN)} popupRef={popupRef} />
        ),
        [KYC_INTRO_SCREEN]: (
            <CommonPage>
                <InfoPage
                    iconName="id-card-80"
                    iconColor="primary"
                    headerText={<DangerouslyHtml dangerouslyHtml={messages.introHeaderTitle} />}
                    secondaryText={<DangerouslyHtml dangerouslyHtml={messages.introText} />}
                    primaryButtonCaption={messages.continueButton}
                    topButtonCaption={messages.notNowButton}
                    topButtonType="link"
                    topButtonLink={AUTH_SEND_MAIN_LINK}
                    primaryButtonOnClick={onContinueIntro}
                />
            </CommonPage>
        ),
        [ONFIDO_DOCUMENTS_SCREEN]: (
            <CommonPage backUrl={AUTH_SEND_MAIN_LINK} leftCloseIcon>
                <HeaderPanel title={messages.titleWithParams} />
                <OnfidoDocList
                    acceptableDocuments={docs?.acceptableDocuments}
                    onClick={onClickDocument}
                    userCountryId={countryId}
                    userCountryInEEA={countryInEEA}
                />
                <Button
                    variant="row"
                    onClick={(e) => {
                        e.preventDefault();
                        props.toggleChat(true);
                    }}
                    caption={messages.needHelpWithDocument}
                    negativeMargin
                />
            </CommonPage>
        ),

        [ONFIDO_DOCUMENT_EXPLANATION_SCREEN]: (
            <CommonPage backUrl={AUTH_SEND_MAIN_LINK} onClickIcon={onClickIcon}>
                <HeaderPanel title={messages.suggestionTitle} subtitle={messages.suggestionSubtitle} />
                <MarkedList>
                    {extra.suggestionItems?.map((item, index) => (
                        <MarkedListItem text={item} key={index} />
                    ))}
                </MarkedList>
                <FixedBottomPanel isRightAligned>
                    <Button
                        type="submit"
                        raised={true}
                        caption={messages.suggestionReady}
                        onClick={() => {
                            amplitude.getInstance().logEvent(AF_REQUESTING_IDENTITY_DOCUMENT_FAQ, { [SCENARIO_ID]: fraudScenarioId });
                            openOnfido();
                        }}
                    />
                </FixedBottomPanel>
            </CommonPage>
        ),
        [ONFIDO_SDK]: (
            <CommonPage backUrl={getBackUrl()} showChat={false}>
                {' '}
                <OnfidoContainer id="onfido-mount" />{' '}
            </CommonPage>
        ),
        [KYC_VERIFYING_SCREEN]: (
            <InfoPage
                iconName="clock-80"
                iconColor="primary"
                headerText={messages.verifyScreenTitle}
                secondaryText={<DangerouslyHtml dangerouslyHtml={messages.verifyScreenText} />}
                primaryButtonCaption={messages.gotItButton}
                primaryButtonType="link"
                primaryButtonLink={AUTH_SEND_MAIN_LINK}
                primaryButtonOnClick={() => {
                    amplitude.getInstance().logEvent(AF_ONFIDO_IN_REVIEW_PROMO_GOT_IT_CLICKED, { [SCENARIO_ID]: fraudScenarioId });
                }}
            />
        ),
    };
    return (
        <>
            {currentScreen ? componentMap[currentScreen] : null}
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, {
    toggleModalWithTimer,
    toggleChat,
})(BasePage(AntiFraudOnfidoPage));
